// Client page (internal) — one screen per company, in four tabs so each job has one place:
// Overview (money, orders, activity), Billing & payments (terms, credit, billing contact, consolidated
// billing), People & access (owners, reps, logins, trusted, active) and Shipping (addresses, customs ID).
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import {
  fetchTimeline,
  companyStatementUrl,
  fetchAssignableOwners,
  setCompanyOwners,
  setCompanyPaymentTerms,
  setCompanyPaymentMethod,
  setCompanyActive,
  setCompanyTrusted,
} from '../api/companies.js';
import { fmtMoney, fmtOrderTotal } from '../lib/matrix.js';
import ShippingAddressCard from '../components/ShippingAddressCard.jsx';
import ShippingCredentialCard from '../components/ShippingCredentialCard.jsx';
import AccountContactsCard from '../components/AccountContactsCard.jsx';
import CompanyBilling from '../components/CompanyBilling.jsx';
import CreditControlCard from '../components/CreditControlCard.jsx';
import ClientPromotionsCard from '../components/ClientPromotionsCard.jsx';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP } from '../lib/access.js';
import { storeName } from '../lib/stores.js';
import { eventLabel, termsLabel } from '../lib/labels.js';
import { ClientLogins, AssignRepForm } from '../components/ClientAccess.jsx';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'billing', label: 'Billing & payments' },
  { key: 'people', label: 'People & access' },
  { key: 'shipping', label: 'Shipping' },
];

function Tabs({ tab, setTab }) {
  return (
    <div role="tablist" aria-label="Client sections" className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
      {TABS.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={tab === t.key}
          onClick={() => setTab(t.key)}
          className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
            tab === t.key ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right text-text-primary">{children}</dd>
    </div>
  );
}

// Orders: a table on wide screens, tappable cards on phones (no sideways scrolling).
function OrdersList({ orders, onOpen }) {
  if (!orders.length) {
    return <div className="rounded-lg border border-line bg-surface p-4 text-sm text-text-muted shadow-sm">No orders yet.</div>;
  }
  return (
    <div className="rounded-lg border border-line bg-surface shadow-sm">
      <h3 className="px-4 pt-3 text-sm font-semibold text-text-primary">Orders</h3>
      <ul className="divide-y divide-line md:hidden">
        {orders.map((o) => (
          <li key={o.id}>
            <button onClick={() => onOpen(o.id)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-raised/40">
              <span className="min-w-0">
                <span className="block font-mono text-xs text-text-secondary">{o.orderNumber || o.id.slice(-8)}</span>
                <span className="text-xs text-text-muted">{new Date(o.createdAt).toLocaleDateString()} · {o.unitCount} units</span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-medium tabular-nums">{fmtOrderTotal(o)}</span>
                <StatusBadge status={o.status} />
              </span>
            </button>
          </li>
        ))}
      </ul>
      <table className="hidden min-w-full text-sm md:table">
        <thead>
          <tr className="border-b border-line text-left text-xs text-text-muted">
            <th className="px-4 py-2 font-medium">Order</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 text-right font-medium">Units</th>
            <th className="px-4 py-2 text-right font-medium">Total</th>
            <th className="px-4 py-2 font-medium">Date</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} onClick={() => onOpen(o.id)} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-raised/40">
              <td className="px-4 py-2 font-mono text-xs text-text-secondary">{o.orderNumber || o.id.slice(-8)}</td>
              <td className="px-4 py-2">
                <StatusBadge status={o.status} />
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{o.unitCount}</td>
              <td className="px-4 py-2 text-right font-medium tabular-nums">{fmtOrderTotal(o)}</td>
              <td className="px-4 py-2 text-xs text-text-muted">{new Date(o.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ClientTimeline() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [owners, setOwners] = useState({ reps: [], staff: [] });
  const [repSel, setRepSel] = useState('');
  const [csSel, setCsSel] = useState('');
  const [terms, setTerms] = useState({ type: 'PAY_IN_FULL' });
  const [method, setMethod] = useState('LINK');
  const [busy, setBusy] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'overview';
  const setTab = (key) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (key === 'overview') n.delete('tab');
        else n.set('tab', key);
        return n;
      },
      { replace: true },
    );

  const load = () =>
    fetchTimeline(companyId)
      .then((d) => {
        setData(d);
        setRepSel(d.company.repOwner?.id || '');
        setCsSel(d.company.csOwner?.id || '');
        setTerms(d.company.defaultPaymentTerms || { type: 'PAY_IN_FULL' });
        setMethod(d.company.paymentMethod || 'LINK');
      })
      .catch((e) => setError(e.response?.data?.error?.message || 'Not found.'));

  useEffect(() => {
    load();
    fetchAssignableOwners().then(setOwners).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const outstanding = () =>
    Object.entries(data?.payments?.summary || {})
      .filter(([, v]) => v.outstanding > 0)
      .map(([c, v]) => fmtMoney(v.outstanding, c))
      .join(', ');

  // Every save on this page reports back: "Saved ✓" or the real error (they used to fail silently).
  const [saveMsg, setSaveMsg] = useState(null); // { key, ok, text }
  const saving = async (key, fn, okText) => {
    setBusy(key);
    setSaveMsg(null);
    try {
      await fn();
      await load();
      setSaveMsg({ key, ok: true, text: okText });
    } catch (e) {
      setSaveMsg({ key, ok: false, text: e.response?.data?.error?.message || 'Could not save — please try again.' });
    } finally {
      setBusy('');
    }
  };
  const SaveNote = ({ k }) =>
    saveMsg?.key === k ? <span className={`text-[11px] ${saveMsg.ok ? 'text-success' : 'text-error'}`}>{saveMsg.text}</span> : null;

  const saveOwners = () => saving('owners', () => setCompanyOwners(companyId, { repUserId: repSel || null, csUserId: csSel || null }), 'Saved ✓');

  // Payment method saves immediately on change (LINK ↔ BANK_TRANSFER).
  const saveMethod = (next) => {
    setMethod(next);
    return saving('method', () => setCompanyPaymentMethod(companyId, next), 'Payment method saved ✓');
  };

  // Payment terms: default fields when switching type, then save (or reset to the global default).
  const termsForType = (type, prev = {}) => {
    if (type === 'DEPOSIT_BALANCE') return { type, depositPct: prev.depositPct ?? 50 };
    if (type === 'NET_TERMS') return { type, netDays: prev.netDays ?? 30 };
    if (type === 'DEPOSIT_NET') return { type, depositPct: prev.depositPct ?? 50, netDays: prev.netDays ?? 30, netFrom: prev.netFrom ?? 'INVOICE' };
    if (type === 'PAY_ON_DELIVERY') return { type: 'PAY_ON_DELIVERY' };
    return { type: 'PAY_IN_FULL' };
  };
  const savePaymentTerms = (clear = false) =>
    saving('terms', async () => {
      let payload = null;
      if (!clear) {
        if (terms.type === 'DEPOSIT_BALANCE') payload = { type: 'DEPOSIT_BALANCE', depositPct: Number(terms.depositPct) || 50 };
        else if (terms.type === 'NET_TERMS') payload = { type: 'NET_TERMS', netDays: Number(terms.netDays) || 30 };
        else if (terms.type === 'DEPOSIT_NET') {
          payload = {
            type: 'DEPOSIT_NET',
            depositPct: Number(terms.depositPct) || 50,
            netDays: Number.isFinite(Number(terms.netDays)) ? Math.round(Number(terms.netDays)) : 30,
            netFrom: terms.netFrom || 'INVOICE',
          };
        }
        else if (terms.type === 'PAY_ON_DELIVERY') payload = { type: 'PAY_ON_DELIVERY' };
        else payload = { type: 'PAY_IN_FULL' };
      }
      await setCompanyPaymentTerms(companyId, payload);
    }, clear ? 'Back to the platform default ✓' : 'Payment terms saved ✓');

  const toggleActive = async () => {
    const active = data.company.isActive;
    if (active) {
      const out = outstanding();
      const msg = out
        ? `Deactivate ${data.company.name}? They still owe ${out}. You'll keep the record and can still chase the balance, but they can't log in or order.`
        : `Deactivate ${data.company.name}? They won't be able to log in or place orders. History is preserved.`;
      if (!window.confirm(msg)) return;
    }
    return saving('active', () => setCompanyActive(companyId, !active), active ? 'Client deactivated' : 'Client reactivated ✓');
  };

  const toggleTrusted = () =>
    saving('trusted', () => setCompanyTrusted(companyId, !data.company.isTrusted), data.company.isTrusted ? 'Trusted turned off' : 'Marked trusted ✓');

  if (error) return <div className="text-sm text-error">{error}</div>;
  if (!data) return <div className="text-sm text-text-muted">Loading…</div>;
  const { company, orders, balances, timeline } = data;
  const loginCount = (company.locations || []).reduce((n, l) => n + (l.contacts || []).length, 0);

  // Start an order for this client: pre-select them in the order context, then open the matrix.
  const startOrder = () => {
    const loc = company.locations?.[0];
    if (!loc) return;
    // Ephemeral hand-off to New Order via router state — no persisted context.
    navigate('/catalog', {
      state: {
        client: {
          store: company.store,
          shopifyCompanyLocationId: loc.shopifyCompanyLocationId,
          companyName: company.name,
          locationName: loc.name,
          onBehalfOfClientUserId: null,
          shopifyCompanyContactId: null,
        },
      },
    });
  };

  return (
    <>
      <button onClick={() => navigate('/clients')} className="mb-3 text-sm text-text-secondary hover:text-text-primary">
        ← Clients
      </button>
      <PageHeader
        title={company.name}
        subtitle={`${storeName(company.store)}${company.isActive === false ? ' · Deactivated' : ''}${company.isTrusted ? ' · Trusted' : ''} · ${loginCount ? `${loginCount} login${loginCount === 1 ? '' : 's'}` : 'No logins yet'}`}
        actions={
          <>
            {can(user, CAP.CREATE_ORDER) && company.locations?.length > 0 && (
              <button
                onClick={startOrder}
                className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
              >
                New order
              </button>
            )}
            <Link
              to={`/clients/${companyId}/home`}
              className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
            >
              See their home page
            </Link>
            <a
              href={companyStatementUrl(companyId)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
            >
              Statement PDF
            </a>
          </>
        }
      />

      <Tabs tab={tab} setTab={setTab} />

      {tab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4">
            {data.credit?.blocked && (
              <div className="rounded-lg border border-error/40 bg-error-light/60 p-3 text-sm">
                <div className="font-semibold text-error">{data.credit.onHold ? 'On hold — can’t place new orders' : 'Over their credit limit'}</div>
                {(data.credit.reasons || []).map((r) => (
                  <p key={r.code} className="mt-0.5 text-xs text-text-secondary">{r.message}</p>
                ))}
                <button onClick={() => setTab('billing')} className="mt-1 text-xs font-medium text-accent hover:underline">
                  Credit settings →
                </button>
              </div>
            )}
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold text-text-primary">Money</h3>
              {/* What they OWE (from the payment schedule) — the question CS actually asks. */}
              {Object.entries(data.payments?.summary || {}).some(([, v]) => v.outstanding > 0) && (
                <div className="mb-3 rounded-md border border-line bg-surface-raised/40 p-2 text-sm">
                  {Object.entries(data.payments.summary)
                    .filter(([, v]) => v.outstanding > 0)
                    .map(([c, v]) => {
                      const overdue = (data.payments.outstanding || []).filter((i) => i.currency === c && i.dueDate && new Date(i.dueDate).getTime() < Date.now()).reduce((a, i) => a + i.amount, 0);
                      return (
                        <div key={c} className="flex justify-between gap-2">
                          <span className="text-text-secondary">Owed ({c})</span>
                          <span className="text-right">
                            <span className="font-semibold">{fmtMoney(v.outstanding, c)}</span>
                            {overdue > 0 && <span className="block text-xs font-medium text-error">{fmtMoney(overdue, c)} overdue</span>}
                          </span>
                        </div>
                      );
                    })}
                  <button onClick={() => navigate(`/payments?companyId=${companyId}`)} className="mt-1 text-xs font-medium text-accent hover:underline">
                    See payments →
                  </button>
                </div>
              )}
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">Order value</div>
              {Object.entries(balances).length ? (
                Object.entries(balances).map(([c, b]) => (
                  <div key={c} className="mb-2 text-sm">
                    <div className="text-xs text-text-muted">{c}</div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Confirmed</span>
                      <span className="font-medium">{fmtMoney(b.approved, c)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Awaiting approval</span>
                      <span className="font-medium text-warning">{fmtMoney(b.pending, c)}</span>
                    </div>
                    <div className="mt-1 flex justify-between border-t border-line pt-1">
                      <span className="text-text-secondary">Total</span>
                      <span className="font-semibold">{fmtMoney(b.total, c)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text-muted">No orders yet.</p>
              )}
            </div>
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold text-text-primary">At a glance</h3>
              <dl className="space-y-1.5 text-sm">
                <Fact label="Status">
                  {company.isActive === false ? <span className="text-error">Deactivated</span> : 'Active'}
                  {company.isTrusted && <span className="ml-1.5 rounded-full bg-accent-light px-2 py-0.5 text-[11px]">Trusted</span>}
                </Fact>
                <Fact label="Sales rep">{company.repOwner ? company.repOwner.name || company.repOwner.email : <span className="text-text-muted">Unassigned</span>}</Fact>
                <Fact label="CS owner">{company.csOwner ? company.csOwner.name || company.csOwner.email : <span className="text-text-muted">Unassigned</span>}</Fact>
                <Fact label="Pays by">{company.paymentMethod === 'BANK_TRANSFER' ? 'Bank transfer' : 'Payment link'}</Fact>
                <Fact label="Terms">{termsLabel(company.defaultPaymentTerms?.type || 'PAY_IN_FULL')}{!company.defaultPaymentTerms && <span className="text-text-muted"> (default)</span>}</Fact>
                <Fact label="Logins">{loginCount || <span className="text-warning">None yet</span>}</Fact>
                <Fact label="Last order">{company.lastOrderAt ? new Date(company.lastOrderAt).toLocaleDateString() : '—'}</Fact>
              </dl>
              <div className="mt-3 flex flex-wrap gap-3 border-t border-line pt-2 text-xs font-medium">
                <button onClick={() => setTab('billing')} className="text-accent hover:underline">Change terms</button>
                <button onClick={() => setTab('people')} className="text-accent hover:underline">Manage people</button>
              </div>
            </div>
            <ClientPromotionsCard companyId={company.id} />
          </div>

          <div className="space-y-4 lg:col-span-2">
            <OrdersList orders={orders} onOpen={(id) => navigate(`/orders/${id}`)} />
              <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
                <h3 className="mb-3 text-sm font-semibold text-text-primary">Activity</h3>
                <ol className="space-y-2.5">
                  {timeline.map((t, i) => (
                    <li key={i} className="flex gap-2 text-xs">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-subtle" />
                      <div>
                        <span className="text-text-primary">{eventLabel(t, t.currency)}</span>{' '}
                        <button
                          onClick={() => navigate(`/orders/${t.orderId}`)}
                          className="font-mono text-text-muted hover:underline"
                        >
                          #{t.orderShort}
                        </button>
                        <div className="text-text-muted">
                          {t.by} · {new Date(t.at).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
          </div>
        </div>
      )}

      {tab === 'billing' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-1 text-sm font-semibold text-text-primary">How they pay</h3>
              <p className="mb-3 text-xs text-text-muted">Used for every new order from this client. Existing orders keep the terms they were placed on.</p>
              {can(user, CAP.MANAGE_PAYMENTS) ? (
                <>
                    <label className="block text-xs text-text-muted">Payment method</label>
                    <select
                      value={method}
                      onChange={(e) => saveMethod(e.target.value)}
                      disabled={busy === 'method'}
                      className="mb-2 mt-0.5 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm"
                    >
                      <option value="LINK">Airwallex payment link</option>
                      <option value="BANK_TRANSFER">Bank transfer (no link created)</option>
                    </select>
                    <label className="block text-xs text-text-muted">Payment terms</label>
                    <select
                      value={terms.type}
                      onChange={(e) => setTerms(termsForType(e.target.value, terms))}
                      className="mt-0.5 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm"
                    >
                      <option value="PAY_IN_FULL">Pay in full (before dispatch)</option>
                      <option value="DEPOSIT_BALANCE">Deposit + balance on delivery</option>
                      <option value="DEPOSIT_NET">Deposit + balance after X days</option>
                      <option value="NET_TERMS">Net terms</option>
                      <option value="PAY_ON_DELIVERY">Pay in full on delivery</option>
                      {terms.type === 'CUSTOM' && <option value="CUSTOM">Custom (set per order)</option>}
                      {terms.type === 'DEPOSIT_NET_FROM_DISPATCH' && <option value="DEPOSIT_NET_FROM_DISPATCH">Consolidated billing terms</option>}
                    </select>
                    {terms.type === 'DEPOSIT_NET' && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
                        <input type="number" min={1} max={99} value={terms.depositPct ?? 50} onChange={(e) => setTerms({ ...terms, depositPct: e.target.value })} aria-label="Deposit percent" className="w-16 rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary" />
                        % upfront, balance
                        <input type="number" min={0} max={365} value={terms.netDays ?? 30} onChange={(e) => setTerms({ ...terms, netDays: e.target.value })} aria-label="Days until balance is due" className="w-16 rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary" />
                        days after
                        <select value={terms.netFrom || 'INVOICE'} onChange={(e) => setTerms({ ...terms, netFrom: e.target.value })} aria-label="Balance clock starts at" className="rounded-md border border-line bg-surface px-2 py-1 text-sm text-text-primary">
                          <option value="INVOICE">invoice date</option>
                          <option value="DISPATCH">dispatch</option>
                          <option value="DELIVERY">delivery</option>
                        </select>
                      </div>
                    )}
                    {terms.type === 'DEPOSIT_BALANCE' && (
                      <label className="mt-1.5 flex items-center gap-2 text-xs text-text-muted">
                        Deposit %
                        <input type="number" min={1} max={99} value={terms.depositPct ?? 50} onChange={(e) => setTerms({ ...terms, depositPct: e.target.value })} className="w-20 rounded-md border border-line bg-surface px-2 py-1 text-sm" />
                        <span className="text-text-muted">balance on delivery</span>
                      </label>
                    )}
                    {terms.type === 'NET_TERMS' && (
                      <label className="mt-1.5 flex items-center gap-2 text-xs text-text-muted">
                        Net
                        <input type="number" min={1} max={365} value={terms.netDays ?? 30} onChange={(e) => setTerms({ ...terms, netDays: e.target.value })} className="w-20 rounded-md border border-line bg-surface px-2 py-1 text-sm" />
                        days
                      </label>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <button onClick={() => savePaymentTerms(false)} disabled={busy === 'terms' || terms.type === 'CUSTOM' || terms.type === 'DEPOSIT_NET_FROM_DISPATCH'} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
                        {busy === 'terms' ? 'Saving…' : 'Save terms'}
                      </button>
                      {data.company.defaultPaymentTerms && (
                        <button onClick={() => savePaymentTerms(true)} disabled={busy === 'terms'} className="text-xs text-text-muted hover:text-text-primary" title="Clear the override — use the platform default">
                          Reset to default
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-[11px] text-text-muted">Applies to this client&rsquo;s future orders. {!data.company.defaultPaymentTerms && 'Currently using the platform default (pay in full before dispatch).'}</p>
                    <SaveNote k="terms" />
                    <SaveNote k="method" />
                </>
              ) : (
                <dl className="space-y-1.5 text-sm">
                  <Fact label="Pays by">{company.paymentMethod === 'BANK_TRANSFER' ? 'Bank transfer' : 'Payment link'}</Fact>
                  <Fact label="Terms">{termsLabel(company.defaultPaymentTerms?.type || 'PAY_IN_FULL')}</Fact>
                </dl>
              )}
            </div>
            <CreditControlCard companyId={companyId} credit={data.credit} canEdit={can(user, CAP.MANAGE_PAYMENTS)} onSaved={load} />
          </div>
          <div className="space-y-4">
            {/* Multi-store chains only — self-hides for single-location clients. */}
            <AccountContactsCard
              companyId={company.id}
              billingEmail={company.billingEmail}
              billingName={company.billingName}
              readOnly={!can(user, CAP.MANAGE_CLIENTS)}
              onChanged={load}
            />
            {can(user, CAP.MANAGE_PAYMENTS) && <CompanyBilling companyId={companyId} company={company} onChanged={load} />}
          </div>
        </div>
      )}

      {tab === 'people' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-1 text-sm font-semibold text-text-primary">Account owners</h3>
              <p className="mb-3 text-xs text-text-muted">The sales rep earns commission and is copied on order updates. The CS owner is who the client hears from.</p>
              <label className="block text-xs text-text-muted">Sales rep</label>
              <select value={repSel} onChange={(e) => setRepSel(e.target.value)} className="mb-2 mt-0.5 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm">
                <option value="">— unassigned —</option>
                {owners.reps.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
              <label className="block text-xs text-text-muted">CS owner</label>
              <select value={csSel} onChange={(e) => setCsSel(e.target.value)} className="mb-3 mt-0.5 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm">
                <option value="">— unassigned —</option>
                {owners.staff.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <button onClick={saveOwners} disabled={busy === 'owners'} className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50">
                  {busy === 'owners' ? 'Saving…' : 'Save owners'}
                </button>
                <SaveNote k="owners" />
              </div>
            </div>

            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-1 text-sm font-semibold text-text-primary">Reps who can see this client</h3>
              <p className="mb-2 text-xs text-text-muted">These reps see the client in their book and can order for them.</p>
              {company.reps.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {(company.repAssignments || []).map((r) => (
                    <span key={r.repUser.id} className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-text-secondary">{r.repUser.name || r.repUser.email}</span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted">No reps yet.</p>
              )}
              {can(user, CAP.MANAGE_CLIENTS) && (
                <button onClick={() => setAssigning((v) => !v)} className="mt-2 text-xs font-medium text-accent hover:underline">
                  {assigning ? 'Close' : '+ Add a rep'}
                </button>
              )}
              {assigning && (
                <AssignRepForm
                  company={company}
                  onDone={() => {
                    setAssigning(false);
                    load();
                  }}
                />
              )}
            </div>

            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-text-primary">Account status</h3>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-text-primary">Trusted client {company.isTrusted ? <span className="text-success">· On</span> : <span className="text-text-muted">· Off</span>}</div>
                  <p className="text-xs text-text-muted">Orders under the approval limit are approved automatically once shipping is confirmed. New clients, big orders and accounts on hold still go to a person.</p>
                  <SaveNote k="trusted" />
                </div>
                {can(user, CAP.MANAGE_CLIENTS) && (
                  <button onClick={toggleTrusted} disabled={busy === 'trusted'} className="shrink-0 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
                    {busy === 'trusted' ? '…' : company.isTrusted ? 'Turn off' : 'Mark trusted'}
                  </button>
                )}
              </div>
              <div className="mt-3 flex items-start justify-between gap-3 border-t border-line pt-3">
                <div>
                  <div className="text-sm font-medium text-text-primary">{company.isActive === false ? 'Deactivated' : 'Active'}</div>
                  <p className="text-xs text-text-muted">
                    {company.isActive === false
                      ? 'Their logins can’t sign in or order. History and balances are kept.'
                      : 'Deactivating stops their logins signing in or ordering. History and balances are kept.'}
                  </p>
                  <SaveNote k="active" />
                </div>
                <button
                  onClick={toggleActive}
                  disabled={busy === 'active'}
                  className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${company.isActive === false ? 'border-line text-success hover:bg-success-light' : 'border-line text-error hover:bg-error-light'}`}
                >
                  {busy === 'active' ? '…' : company.isActive === false ? 'Reactivate' : 'Deactivate'}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
            <h3 className="mb-1 text-sm font-semibold text-text-primary">Who can log in</h3>
            <p className="mb-3 text-xs text-text-muted">Each person gets their own login. Invitations email a link to set up the account.</p>
            <ClientLogins company={company} onChange={load} readOnly={!can(user, CAP.MANAGE_CLIENTS)} />
          </div>
        </div>
      )}

      {tab === 'shipping' && (
        <div className="grid gap-4 lg:grid-cols-2">
          {(company.locations || []).map((loc) => (
            <div key={loc.shopifyCompanyLocationId} className="space-y-4">
              <ShippingAddressCard
                scope={{ store: company.store, companyLocationId: loc.shopifyCompanyLocationId }}
                title={company.locations.length > 1 ? `Ships to — ${loc.name}` : 'Shipping address'}
              />
              {/* Only renders when the destination country needs a customs ID (or one is on file). */}
              <ShippingCredentialCard
                scope={{ store: company.store, companyLocationId: loc.shopifyCompanyLocationId }}
                readOnly={!can(user, CAP.MANAGE_SHIPPING)}
              />
            </div>
          ))}
          {!company.locations?.length && <p className="text-sm text-text-muted">No locations synced from Shopify yet.</p>}
        </div>
      )}
    </>
  );
}
